import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardShell from "../components/DashboardShell";
import MediaUploader from "../components/MediaUploader";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const SOCIAL_FIELDS = [
  { key: "linkedin", placeholder: "https://linkedin.com/in/…" },
  { key: "twitter", placeholder: "https://x.com/…" },
  { key: "facebook", placeholder: "https://facebook.com/…" },
  { key: "instagram", placeholder: "https://instagram.com/…" },
  { key: "github", placeholder: "https://github.com/…" },
  { key: "website", placeholder: "https://yoursite.com" },
  { key: "hackthebox", placeholder: "https://app.hackthebox.com/…" },
  { key: "tryhackme", placeholder: "https://tryhackme.com/p/…" },
];

export default function ProfileEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const { user, refreshUser } = useAuth();
  const isOrg = !!params.orgId;
  const targetId = params.orgId || user?.id;
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [avatarFileId, setAvatarFileId] = useState(null);
  const [bannerFileId, setBannerFileId] = useState(null);
  const [handle, setHandle] = useState("");
  const [handleError, setHandleError] = useState("");
  const [handleSaving, setHandleSaving] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    const endpoint = isOrg ? `/profile/org/${targetId}` : `/profile/user/${targetId}`;
    api.get(endpoint).then((r) => {
      const p = r.data;
      setProfile({
        ...p,
        headline: p.headline || "",
        bio: p.bio || "",
        social_links: p.social_links || {},
        phone: p.phone || "",
        address: p.address || "",
        organization_affiliation: p.organization_affiliation || "",
        profile_visibility: p.profile_visibility || "public",
      });
      setHandle(p.handle || "");
    }).catch((e) => setError(formatApiError(e)));
  }, [targetId, isOrg]);

  if (!profile) {
    return (
      <DashboardShell>
        <div className="csc-card p-8 font-mono-tech text-xs text-[#94a3b8] uppercase tracking-wider">Loading…</div>
      </DashboardShell>
    );
  }

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const updateSocial = (k, v) => setProfile((p) => ({ ...p, social_links: { ...p.social_links, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const body = {
        headline: profile.headline,
        bio: profile.bio,
        social_links: profile.social_links,
        avatar_file_id: avatarFileId,
        banner_file_id: bannerFileId,
      };
      if (isOrg) {
        body.phone = profile.phone;
        body.address = profile.address;
      } else {
        body.organization_affiliation = profile.organization_affiliation;
        body.profile_visibility = profile.profile_visibility;
      }
      const endpoint = isOrg ? `/profile/org/${targetId}` : `/profile/me`;
      await api.patch(endpoint, body);
      await refreshUser();
      navigate(isOrg ? `/org/${targetId}` : `/profile/me`, { replace: true });
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setHandleOnce = async () => {
    setHandleError(""); setHandleSaving(true);
    try {
      await api.post(`/profile/set-handle`, { handle });
      await refreshUser();
      navigate(`/u/${handle}`, { replace: true });
    } catch (e) { setHandleError(formatApiError(e)); }
    finally { setHandleSaving(false); }
  };

  const charCount = (profile.bio || "").length;
  const charColor = charCount > 980 ? "text-red-400" : charCount > 900 ? "text-amber-400" : "text-[#94a3b8]";

  return (
    <DashboardShell>
      <form onSubmit={save} className="space-y-5 max-w-3xl mx-auto" data-testid="profile-edit-page">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Edit Profile</div>
          <h1 className="text-2xl lg:text-3xl font-semibold mt-1">{isOrg ? "Organization Profile" : "Your Profile"}</h1>
        </div>

        <section className="csc-card overflow-hidden">
          <div className="relative h-44 lg:h-56" data-testid="banner-edit">
            <MediaUploader
              kind="banner"
              currentUrl={profile.banner_url}
              onUploaded={(id, url) => { setBannerFileId(id); setProfile((p) => ({ ...p, banner_url: url })); }}
              label="Change banner"
              testId="banner-uploader"
            />
          </div>
          <div className="px-6 pb-6 -mt-12 flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="w-24 h-24 lg:w-28 lg:h-28 rounded-full overflow-hidden border-4 border-[#0d1b2e]">
              <MediaUploader
                kind="avatar"
                shape="round"
                currentUrl={profile.avatar_url}
                onUploaded={(id, url) => { setAvatarFileId(id); setProfile((p) => ({ ...p, avatar_url: url })); }}
                label="Avatar"
                testId="avatar-uploader"
              />
            </div>
            <div className="flex-1">
              <label className="csc-label">Headline</label>
              <input className="csc-input" value={profile.headline} onChange={(e) => update("headline", e.target.value)} placeholder="e.g. Cybersecurity Analyst | Volunteer" data-testid="headline-input" maxLength={140} />
            </div>
          </div>
        </section>

        {!isOrg && (
          <section className="csc-card p-5" data-testid="handle-section">
            <label className="csc-label">Handle (one-time change)</label>
            <div className="flex gap-2">
              <input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))} className="csc-input" placeholder="your_handle" disabled={user?.handle_locked} data-testid="handle-input" />
              <button type="button" onClick={setHandleOnce} disabled={handleSaving || user?.handle_locked || !handle} className="csc-btn-ghost" data-testid="set-handle-btn">
                {handleSaving ? "Saving…" : (user?.handle_locked ? "Locked" : "Save handle")}
              </button>
            </div>
            <p className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8] mt-2">
              Lowercase letters, numbers and underscores. You can change it ONCE.
            </p>
            {handleError && <div className="text-xs text-red-400 mt-2">{handleError}</div>}
          </section>
        )}

        <section className="csc-card p-5 space-y-3">
          <label className="csc-label">Bio (max 1000 chars)</label>
          <textarea
            value={profile.bio}
            onChange={(e) => update("bio", e.target.value.slice(0, 1000))}
            rows={5}
            className="csc-input resize-none"
            placeholder={isOrg ? "Tell people about your nonprofit…" : "Tell the community a bit about yourself…"}
            data-testid="bio-input"
          />
          {/* TODO: Upgrade to Tiptap rich text editor post-launch if users request formatting. */}
          <div className={`text-right text-[11px] font-mono-tech ${charColor}`} data-testid="bio-counter">
            {charCount} / 1000
          </div>
        </section>

        {!isOrg ? (
          <section className="csc-card p-5 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="csc-label">Organization Affiliation</label>
                <input className="csc-input" value={profile.organization_affiliation} onChange={(e) => update("organization_affiliation", e.target.value)} data-testid="org-affiliation-input" />
              </div>
              <div>
                <label className="csc-label">Profile Visibility</label>
                <select className="csc-input" value={profile.profile_visibility} onChange={(e) => update("profile_visibility", e.target.value)} data-testid="visibility-select">
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
          </section>
        ) : (
          <section className="csc-card p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="csc-label">Public Address</label>
              <input className="csc-input" value={profile.address} onChange={(e) => update("address", e.target.value)} data-testid="address-input" />
            </div>
            <div>
              <label className="csc-label">Public Phone</label>
              <input className="csc-input" value={profile.phone} onChange={(e) => update("phone", e.target.value)} data-testid="phone-input" />
            </div>
          </section>
        )}

        <section className="csc-card p-5 space-y-3">
          <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">Social Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {SOCIAL_FIELDS.map((s) => (
              <div key={s.key}>
                <label className="csc-label">{s.key}</label>
                <input
                  className="csc-input"
                  value={profile.social_links[s.key] || ""}
                  onChange={(e) => updateSocial(s.key, e.target.value)}
                  placeholder={s.placeholder}
                  data-testid={`social-${s.key}-input`}
                />
              </div>
            ))}
          </div>
        </section>

        {error && <div className="text-sm text-red-400 csc-card p-3" data-testid="profile-edit-error">{error}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate(-1)} className="csc-btn-ghost">Cancel</button>
          <button disabled={saving} className="csc-btn-primary" data-testid="profile-save-btn">
            {saving ? "Saving…" : "Save Profile"}
          </button>
        </div>
      </form>
    </DashboardShell>
  );
}
